import { useState, useMemo } from 'react';
import { useGameState } from '../GameProvider';
import { evaluate5, evaluate3, compareHands, getHandName5, getHandName3, validateArrangement } from '../../gameEngine';
import Card from './Card';
import './ComparisonModal.css';

export default function ComparisonModal({ targetUid, onClose }) {
  const { uid, players, revealedArrangements, roundResults, activeRules } = useGameState();

  const opponentUids = useMemo(() =>
    Object.keys(revealedArrangements || {}).filter(id => id !== uid),
    [revealedArrangements, uid]
  );

  const [currentIdx, setCurrentIdx] = useState(() =>
    Math.max(0, opponentUids.indexOf(targetUid))
  );

  const opponentUid = opponentUids[currentIdx];
  if (!opponentUid) return null;

  const myArr = revealedArrangements[uid];
  const oppArr = revealedArrangements[opponentUid];
  if (!myArr || !oppArr) return null;

  const myResult = roundResults?.[uid];
  const breakdown = myResult?.breakdown?.[opponentUid];

  const rows = ['top', 'middle', 'bottom'];
  const rowSizes = { top: 3, middle: 5, bottom: 5 };

  const getEval = (cards, size) => size === 3 ? evaluate3(cards) : evaluate5(cards);
  const getName = (cards, size) => {
    const e = getEval(cards, size);
    return size === 3 ? getHandName3(e) : getHandName5(e);
  };

  const prev = () => setCurrentIdx((currentIdx - 1 + opponentUids.length) % opponentUids.length);
  const next = () => setCurrentIdx((currentIdx + 1) % opponentUids.length);

  // Foul detection
  const myFouled = myArr.bottom?.length === 5 && myArr.middle?.length === 5 && myArr.top?.length === 3
    ? !validateArrangement(myArr).valid : false;
  const oppFouled = oppArr.bottom?.length === 5 && oppArr.middle?.length === 5 && oppArr.top?.length === 3
    ? !validateArrangement(oppArr).valid : false;

  // Individual roundhouse — show if either player won all 3 rows (and rule is enabled)
  const iWonAll3 = breakdown?.rows?.every(r => r > 0);
  const theyWonAll3 = breakdown?.rows?.every(r => r < 0);
  const isIndividualRoundhouse = activeRules?.individualRoundHouse && (iWonAll3 || theyWonAll3);
  const roundhouseWinnerName = iWonAll3
    ? (players[uid]?.displayName || 'You')
    : (players[opponentUid]?.displayName);

  return (
    <div className="comparison-overlay" onClick={onClose}>
      <div className="comparison-modal" onClick={e => e.stopPropagation()}>
        {/* Navigation — opponent name uses same color as opponent column */}
        <div className="comparison-nav">
          {opponentUids.length > 1 && (
            <button className="comparison-nav__arrow" onClick={prev}>&larr;</button>
          )}
          <span className="comparison-nav__name comparison-header--opp">{players[opponentUid]?.displayName}</span>
          {opponentUids.length > 1 && (
            <button className="comparison-nav__arrow" onClick={next}>&rarr;</button>
          )}
        </div>

        {/* Dots */}
        {opponentUids.length > 1 && (
          <div className="comparison-dots">
            {opponentUids.map((id, i) => (
              <span key={id} className={`comparison-dot ${i === currentIdx ? 'comparison-dot--active' : ''}`} />
            ))}
          </div>
        )}

        {isIndividualRoundhouse && (
          <div className="comparison-roundhouse-badge">Individual Round House — {roundhouseWinnerName}</div>
        )}

        {/* Player name headers + foul indicators */}
        <div className="comparison-headers">
          <div className="comparison-header-col">
            <span className="comparison-header comparison-header--me">{players[uid]?.displayName || 'You'}</span>
            {myFouled && <span className="comparison-foul-badge">FOUL</span>}
          </div>
          <span className="comparison-header-vs">vs</span>
          <div className="comparison-header-col">
            <span className="comparison-header comparison-header--opp">{players[opponentUid]?.displayName}</span>
            {oppFouled && <span className="comparison-foul-badge">FOUL</span>}
          </div>
        </div>

        {/* Row comparisons — points come from the SERVER's scoring breakdown
            (bonus- and foul-aware), never recomputed locally. The engine's
            breakdown arrays are ordered [bottom, middle, top]. */}
        {rows.map((row) => {
          const size = rowSizes[row];
          const myCards = myArr[row] || [];
          const oppCards = oppArr[row] || [];
          const engineIdx = { bottom: 0, middle: 1, top: 2 }[row];

          // Chi modes carry signed rowPoints (with bonuses); Basic carries
          // signed rows (±1 win/loss). Fall back to a local compare only when
          // no server breakdown exists (e.g. viewer sat out this round).
          let myRowPts;
          if (breakdown?.rowPoints) myRowPts = breakdown.rowPoints[engineIdx] ?? 0;
          else if (breakdown?.rows) myRowPts = breakdown.rows[engineIdx] ?? 0;
          else myRowPts = compareHands(getEval(myCards, size), getEval(oppCards, size));
          const oppRowPts = -myRowPts;

          const formatPts = (pts) => pts > 0 ? `+${pts}` : pts < 0 ? `${pts}` : '0';

          return (
            <div key={row} className="comparison-row">
              {/* My side */}
              <div className={`comparison-side ${myRowPts > 0 ? 'comparison-side--win' : myRowPts < 0 ? 'comparison-side--lose' : ''}`}>
                <div className="comparison-cards">
                  {myCards.map((c, i) => <Card key={c.id || i} card={c} size="small" disabled />)}
                </div>
                <span className="comparison-hand-name">{row.toUpperCase()} - {getName(myCards, size)}</span>
                <span className={`comparison-points ${myRowPts > 0 ? 'positive' : myRowPts < 0 ? 'negative' : ''}`}>
                  {formatPts(myRowPts)}
                </span>
              </div>

              {/* Opponent side */}
              <div className={`comparison-side ${oppRowPts > 0 ? 'comparison-side--win' : oppRowPts < 0 ? 'comparison-side--lose' : ''}`}>
                <div className="comparison-cards">
                  {oppCards.map((c, i) => <Card key={c.id || i} card={c} size="small" disabled />)}
                </div>
                <span className="comparison-hand-name">{row.toUpperCase()} - {getName(oppCards, size)}</span>
                <span className={`comparison-points ${oppRowPts > 0 ? 'positive' : oppRowPts < 0 ? 'negative' : ''}`}>
                  {formatPts(oppRowPts)}
                </span>
              </div>
            </div>
          );
        })}

        {/* Total */}
        <div className="comparison-total">
          {(() => {
            const myTotal = breakdown?.points || 0;
            const oppTotal = -myTotal;
            const fmt = (p) => p > 0 ? `+${p}` : `${p}`;
            return (
              <div className="comparison-total__row">
                <span className={`comparison-total__score ${myTotal > 0 ? 'positive' : myTotal < 0 ? 'negative' : ''}`}>
                  {fmt(myTotal)}
                </span>
                <span className="comparison-total__label">Total</span>
                <span className={`comparison-total__score ${oppTotal > 0 ? 'positive' : oppTotal < 0 ? 'negative' : ''}`}>
                  {fmt(oppTotal)}
                </span>
              </div>
            );
          })()}
        </div>

        <button className="comparison-close" onClick={onClose}>✕</button>
      </div>
    </div>
  );
}
